import type { KeyboardEvent, ReactNode } from "react";

import { Box, Card, Group, Skeleton, Text, Title } from "@mantine/core";
import { IconAlertCircle, IconAlertTriangle } from "@tabler/icons-react";
import clsx from "clsx";

import type { MetricTone } from "./targetProgress";

import { activateOnKey } from "./activateOnKey";
import cardContentStyles from "./cardContent.module.css";
import styles from "./metricCard.module.css";

const TONE_CLASS: Record<MetricTone, string> = {
    positive: styles.tonePositive,
    warning: styles.toneWarning,
    negative: styles.toneNegative,
};

// On-track needs no glyph; the two off-target tones lead with a warning icon (per the pill spec).
const TONE_ICON: Partial<Record<MetricTone, typeof IconAlertTriangle>> = {
    warning: IconAlertTriangle,
    negative: IconAlertCircle,
};

interface Props {
    title: string;
    value: ReactNode;
    footer: ReactNode;
    isLoading: boolean;
    isEmpty: boolean;
    emptyValue: ReactNode;
    status?: string;
    tone?: MetricTone;
    delta?: string;
    /** When false, render {footer} bare (no "Target: " prefix) for cards without a hard target. */
    showTargetLabel?: boolean;
    /** When provided, the card becomes a button that opens its detail panel. */
    onClick?: () => void;
}

export function MetricCard({
    title,
    value,
    footer,
    isLoading,
    isEmpty,
    emptyValue,
    status,
    tone,
    delta,
    showTargetLabel = true,
    onClick,
}: Props) {
    let valueRow: ReactNode;
    if (isLoading) {
        valueRow = <Skeleton height={42} width={120} radius="sm" />;
    } else if (isEmpty) {
        valueRow = <Text className={cardContentStyles.emptyState}>{emptyValue}</Text>;
    } else {
        valueRow = <Box className={styles.value}>{value}</Box>;
    }

    // Status pill lives top-right in the card head; only meaningful once the card has real data .
    const ToneIcon = tone ? TONE_ICON[tone] : undefined;
    const statusBadge =
        !isLoading && !isEmpty && status && tone ? (
            <Text component="span" className={clsx(styles.statusBadge, TONE_CLASS[tone])}>
                {ToneIcon && <ToneIcon size={14} stroke={2.5} aria-hidden />}
                {status}
            </Text>
        ) : null;

    const interactiveProps = onClick
        ? {
              role: "button",
              tabIndex: 0,
              onClick,
              onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => activateOnKey(event, onClick),
          }
        : {};

    return (
        <Card classNames={{ root: clsx(styles.root, onClick && styles.interactive) }} {...interactiveProps}>
            <Group justify="space-between" align="center" wrap="nowrap" className={styles.titleRow}>
                <Title order={3} className={styles.title}>
                    {title}
                </Title>
                {statusBadge}
            </Group>
            <Box className={styles.valueRow}>{valueRow}</Box>
            <Text className={styles.target}>{showTargetLabel ? <>Target: {footer}</> : footer}</Text>
            {!isEmpty && !isLoading && delta && <Text className={styles.gap}>{delta}</Text>}
        </Card>
    );
}
